import React, { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { useDeepMemo } from '../../../hooks';
import { SaveQuickReport } from '../DataTableAdvancedElements/DataTableQuickReports/utils/DataTableQuickReports.types';
import { OnQuickReportsChanged, QueryModel, SortModel } from './DataTableQueryProvider/DataTableQuery.types';
import { useDataTableQuickReportsContext } from './DataTableQuickReportsContext';
import { useDataTableQueryContext } from './DataTableQueryProvider/DataTableQueryProvider';
import { DATA_TABLE_LIMIT } from './getDataTableCount';

interface DataTableQuickActionsContextProps<Q extends QueryModel, S extends SortModel> {
  selectQuickReport: (quickReportId: string, toggle?: boolean) => void;
  duplicateQuickReport: () => void;
  saveNewQuickReport: () => void;
  quickReport?: SaveQuickReport;
  setQuickReport: (quickReport: SaveQuickReport) => void;
  editQuickReportName: () => void;
  saveCurrentQuickReport: () => void;
  deleteQuickReport: () => void;
  undoChanges: () => void;
  saveAsQuickReport: () => void;
  unPinQuickReport: (quickReportId: string) => void;
  reOrderQuickReports: (newReportsOrder: OnQuickReportsChanged<Q, S>[]) => void;
  pinAllQuickReports: () => void;
  unPinAllQuickReports: () => void;
  pinQuickReport: (quickReportId: string) => void;
}

const DataTableQuickActionsContext = createContext<DataTableQuickActionsContextProps<any, any>>({
  selectQuickReport: () => {},
  duplicateQuickReport: () => {},
  saveNewQuickReport: () => {},
  quickReport: undefined,
  setQuickReport: () => {},
  editQuickReportName: () => {},
  saveCurrentQuickReport: () => {},
  deleteQuickReport: () => {},
  undoChanges: () => {},
  saveAsQuickReport: () => {},
  unPinQuickReport: () => {},
  reOrderQuickReports: () => {},
  pinAllQuickReports: () => {},
  unPinAllQuickReports: () => {},
  pinQuickReport: () => {}
});

interface DataTableQuickActionsProviderProps {}

export const DataTableQuickActionsProvider = <Q extends QueryModel, S extends SortModel>({
  children
}: PropsWithChildren<DataTableQuickActionsProviderProps>) => {
  const {
    setActiveQuickReportId,
    setUnSavedQuickReport,
    setQuickReports,
    quickReports,
    activeQuickReportId,
    activeQuickReport
  } = useDataTableQuickReportsContext<Q, S>();
  const { queryVariables, applyQueryVariables } = useDataTableQueryContext<Q, S>();
  const [quickReport, setQuickReport] = useState<SaveQuickReport>();

  const undoChanges = useCallback(() => {
    applyQueryVariables({
      query: activeQuickReport?.query,
      sort: activeQuickReport?.sort,
      page: { limit: queryVariables?.page?.limit ?? DATA_TABLE_LIMIT, offset: 0 },
      quickReportId: activeQuickReport?.quickReportId
    });
  }, [
    activeQuickReport?.query,
    activeQuickReport?.quickReportId,
    activeQuickReport?.sort,
    applyQueryVariables,
    queryVariables?.page?.limit
  ]);

  const reOrderQuickReports = useCallback(
    (newFiltersOrder: OnQuickReportsChanged<Q, S>[]) => {
      setQuickReports((prevState) => {
        const newUniqueQuickReportsConfig = newFiltersOrder.map((report, index) => ({
          ...report,
          quickReportId: report.quickReportId,
          relativePlacement: index,
          pinned: prevState?.find((el) => el.quickReportId === report.quickReportId)?.pinned ?? false
        }));
        return newUniqueQuickReportsConfig;
      });
    },
    [setQuickReports]
  );

  const pinAllQuickReports = useCallback(() => {
    setQuickReports((prevState) => {
      const newUniqueQuickReportsConfig = prevState?.map((report) => ({ ...report, pinned: true }));
      return newUniqueQuickReportsConfig;
    });
  }, [setQuickReports]);

  const unPinAllQuickReports = useCallback(() => {
    setQuickReports((prevState) => {
      const newUniqueQuickReportsConfig = prevState?.map((report) => ({ ...report, pinned: false }));
      return newUniqueQuickReportsConfig;
    });
  }, [setQuickReports]);

  const pinQuickReport = useCallback(
    (quickReportId: string) => {
      setQuickReports((prevState) => {
        const newUniqueQuickReportsConfig =
          prevState?.map((prevStateFilterId) =>
            prevStateFilterId.quickReportId === quickReportId
              ? { ...prevStateFilterId, quickReportId, pinned: true }
              : prevStateFilterId
          ) ?? [];
        return (
          newUniqueQuickReportsConfig
            // sort pinned reports first
            .sort((x, y) => Number(y.pinned) - Number(x.pinned))
            .map((report, index) => ({ ...report, relativePlacement: index }))
        );
      });
    },
    [setQuickReports]
  );

  const unPinQuickReport = useCallback(
    (quickReportId: string) => {
      setQuickReports((prevState) => {
        const newUniqueQuickReportsConfig =
          prevState?.map((prevStateFilterId) =>
            prevStateFilterId.quickReportId === quickReportId
              ? { ...prevStateFilterId, quickReportId, pinned: false }
              : prevStateFilterId
          ) ?? [];
        return (
          newUniqueQuickReportsConfig
            // sort pinned reports first
            .sort((x, y) => Number(y.pinned) - Number(x.pinned))
            .map((report, index) => ({ ...report, relativePlacement: index }))
        );
      });
    },
    [setQuickReports]
  );

  const saveNewQuickReport = useCallback(() => {
    // Set for the local management random quickReportId.
    const quickReportId = new Date().getTime() + Math.random().toString();
    const newState =
      quickReports?.map((report, index) => ({
        ...report,
        default: false,
        relativePlacement: index + 1
      })) ?? [];

    const nextQuickReports = [
      {
        ...queryVariables,
        relativePlacement: 0,
        quickReportId,
        name: quickReport?.name,
        default: true,
        pinned: !!quickReport?.pinned
      } as OnQuickReportsChanged<Q, S>,
      ...newState
    ];
    setQuickReports(nextQuickReports);
    setActiveQuickReportId(quickReportId, nextQuickReports);
    setQuickReport(undefined);
    setUnSavedQuickReport(undefined);
  }, [
    queryVariables,
    quickReport?.name,
    quickReport?.pinned,
    quickReports,
    setActiveQuickReportId,
    setQuickReports,
    setUnSavedQuickReport
  ]);

  const saveAsQuickReport = useCallback(() => saveNewQuickReport(), [saveNewQuickReport]);

  const saveCurrentQuickReport = useCallback(() => {
    setQuickReports((prevState) =>
      prevState?.map((report) => {
        if (report.quickReportId !== activeQuickReportId) return { ...report, default: false };
        return {
          ...report,
          ...queryVariables,
          default: true
        } as OnQuickReportsChanged<Q, S>;
      })
    );
    setUnSavedQuickReport(undefined);
    setQuickReport(undefined);
  }, [activeQuickReportId, queryVariables, setQuickReports, setUnSavedQuickReport]);

  const duplicateQuickReport = useCallback(() => {
    const quickReportId = new Date().getTime() + Math.random().toString();
    const reportToDuplicate = quickReports?.find((report) => report.quickReportId === quickReport?.quickReportId);
    if (reportToDuplicate) {
      const newState =
        quickReports?.map((report, index) => ({
          ...report,
          default: false,
          relativePlacement: index + 1
        })) ?? [];

      const nextQuickReports = [
        {
          ...reportToDuplicate,
          relativePlacement: 0,
          quickReportId,
          name: quickReport?.name,
          default: true,
          uid: ''
        } as OnQuickReportsChanged<Q, S>,
        ...newState
      ];

      setQuickReports(nextQuickReports);
      setActiveQuickReportId(quickReportId, nextQuickReports);
    } else {
      setActiveQuickReportId(undefined);
    }
    setQuickReport(undefined);
    setUnSavedQuickReport(undefined);
  }, [
    quickReport?.name,
    quickReport?.quickReportId,
    quickReports,
    setActiveQuickReportId,
    setQuickReports,
    setUnSavedQuickReport
  ]);

  const editQuickReportName = useCallback(() => {
    const quickReportName = quickReport?.name;
    if (quickReportName) {
      setQuickReports((prevState) =>
        prevState?.map((report) => {
          if (report.quickReportId !== quickReport?.quickReportId) return report;
          return {
            ...report,
            name: quickReportName
          };
        })
      );
      setQuickReport(undefined);
    }
  }, [quickReport?.quickReportId, quickReport?.name, setQuickReports]);

  const deleteQuickReport = useCallback(() => {
    if (activeQuickReportId === quickReport?.quickReportId) {
      setActiveQuickReportId(undefined);
    }
    setQuickReports((prevState) =>
      prevState
        ?.filter((report) => report.quickReportId !== quickReport?.quickReportId)
        .map((report, index) => ({ ...report, relativePlacement: index }))
    );
    setQuickReport(undefined);
  }, [activeQuickReportId, quickReport?.quickReportId, setQuickReports, setActiveQuickReportId]);

  const selectQuickReport = useCallback(
    (quickReportId: string, toggle: boolean = false) => {
      const isActive = activeQuickReportId === quickReportId;
      setActiveQuickReportId(toggle && isActive ? undefined : quickReportId);
      setQuickReports((prevState) =>
        prevState?.map((report) => {
          if (report.quickReportId !== quickReportId) return { ...report, default: false };
          return {
            ...report,
            default: toggle && isActive ? !report.default : true
          };
        })
      );
    },
    [activeQuickReportId, setActiveQuickReportId, setQuickReports]
  );

  const value = useDeepMemo(
    (): DataTableQuickActionsContextProps<Q, S> => ({
      selectQuickReport,
      duplicateQuickReport,
      saveNewQuickReport,
      quickReport,
      setQuickReport,
      editQuickReportName,
      saveCurrentQuickReport,
      deleteQuickReport,
      undoChanges,
      saveAsQuickReport,
      unPinQuickReport,
      reOrderQuickReports,
      pinAllQuickReports,
      unPinAllQuickReports,
      pinQuickReport
    }),
    [
      selectQuickReport,
      duplicateQuickReport,
      saveNewQuickReport,
      quickReport,
      editQuickReportName,
      saveCurrentQuickReport,
      deleteQuickReport,
      undoChanges,
      saveAsQuickReport,
      unPinQuickReport,
      reOrderQuickReports,
      pinAllQuickReports,
      unPinAllQuickReports,
      pinQuickReport
    ]
  );

  return <DataTableQuickActionsContext.Provider value={value}>{children}</DataTableQuickActionsContext.Provider>;
};

export const useDataTableQuickActionsContext = <
  Q extends QueryModel,
  S extends SortModel
>(): DataTableQuickActionsContextProps<Q, S> => {
  const context = useContext<DataTableQuickActionsContextProps<Q, S>>(DataTableQuickActionsContext);

  if (!context) {
    throw new Error('useDataTableQuickActionsContext must be used under DataTableQuickActionsContext');
  }
  return context;
};
